import Image from 'next/image';
import { useMemo } from 'react';
import type { PickemDto } from '@/api/backoffice-schemas';
import type { EnhancedPickemDto } from '@/components/pickems/types';
import { MatchStatusText, MatchStatusType } from '@/constants/match.const';
import { cn } from '@/utils/cn';
import { formatMatchTime } from '@/utils/matches';
import { getWinningTeamId } from '@/utils/pickems';
import { PickemTeamButtonSelect } from './PickemTeamButtonSelect';

interface BettablePickemItemProps {
  item: PickemDto;
  selectedPickems: EnhancedPickemDto[];
  onSelect: (pickemId: string, winnerId: string) => void;
}

const BettablePickemItem = ({
  item,
  onSelect,
  selectedPickems,
}: BettablePickemItemProps) => {
  const pickemInSelected = selectedPickems.find(
    (pickem) => pickem.id === item.id
  );

  const { isBetPlaced, isExpired, match, winnerChoiceId } = item;

  const isEndOfMatch = match?.status === MatchStatusType.FINISHED;

  const winningTeamId = getWinningTeamId(match);

  const isPlacedBetWinning =
    isBetPlaced && isEndOfMatch && winnerChoiceId === winningTeamId;
  const isPlacedBetLosing =
    isBetPlaced && isEndOfMatch && winnerChoiceId !== winningTeamId;

  const formattedTime = useMemo(() => {
    return formatMatchTime(item.match?.beginAt);
  }, [item.match?.beginAt]);

  // Early return if match data isn't available
  if (!item.match?.teamMatches || item.match.teamMatches.length < 2) {
    return (
      <div className="flex items-center justify-center rounded-lg bg-card-bg px-8 py-5">
        <span className="text-gray">Match data unavailable</span>
      </div>
    );
  }

  const [team1, team2] = item.match.teamMatches;

  return (
    <div className="flex flex-col gap-5 rounded-lg bg-card-bg px-8 py-5 md:justify-between">
      <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
        <div className="flex items-center gap-4">
          {item.match.league?.imageUrl && (
            <Image
              alt={item.match.league?.name ?? 'League'}
              height={36}
              src={item.match.league.imageUrl}
              width={36}
            />
          )}
          <div className="flex flex-col">
            <span className="hidden md:inline-block">
              BO{item.match.numberOfGames ?? '?'}
            </span>
            <span className="text-gray text-sm md:text-xs">
              {item.match.league?.name ?? 'Unknown League'} Season{' '}
              {item.match.serie?.season} {item.match.serie?.name}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <span className="font-semibold text-sm">{formattedTime}</span>

          <div className="flex items-center gap-2">
            <div
              className={cn(
                'rounded-md px-1.5 py-0.5 text-center font-semibold text-black text-xs',
                isBetPlaced && 'bg-green',
                isExpired && !isBetPlaced && 'bg-red text-white',
                !(isBetPlaced || isExpired) && 'bg-yellow-300'
              )}
            >
              {isExpired && !isBetPlaced && 'Betting closed'}
              {isBetPlaced && 'Bet placed'}
              {!(isBetPlaced || isExpired) && 'Bet not placed'}
            </div>

            <div className="rounded-md bg-blue-gray px-1.5 py-0.5 text-center font-semibold text-black text-xs">
              {
                MatchStatusText[
                  item.match.status as keyof typeof MatchStatusText
                ]
              }
            </div>
          </div>
        </div>
      </div>

      <div className="flex grow flex-col items-center gap-4 md:justify-between lg:flex-row">
        <PickemTeamButtonSelect
          isSelected={pickemInSelected?.selectedWinnerId === team1.teamId}
          item={item}
          onClick={() => onSelect(item.id ?? '', team1.teamId as string)}
          side="left"
          team={team1}
        />

        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-gray font-semibold text-dark-gray text-xs">
          VS
        </div>

        <PickemTeamButtonSelect
          isSelected={pickemInSelected?.selectedWinnerId === team2.teamId}
          item={item}
          onClick={() => onSelect(item.id ?? '', team2.teamId as string)}
          side="right"
          team={team2}
        />
      </div>

      {isPlacedBetWinning && (
        <div className="text-center font-semibold text-green text-sm">
          You won a bet on this match!
        </div>
      )}
      {isPlacedBetLosing && (
        <div className="text-center font-semibold text-red text-sm">
          You lost a bet on this match!
        </div>
      )}
    </div>
  );
};

export default BettablePickemItem;
